import { Component, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ShareService } from '../../growth/share.service';
import { ShareModelComponent } from '../../growth/share-model/share-model.component';
import { BecomeModelDialogComponent } from '../../support/become-model-dialog.component';
import { AuthService } from '../../auth/auth.service';
import { Observable, of, EMPTY } from 'rxjs';
import { Router, NavigationEnd } from '@angular/router';
import { filter, switchMap, catchError, tap } from 'rxjs/operators';
import { UserService } from '../../auth/user.service';
import { User } from '../../models/user';
import { ModelService } from '../../product/model.service';
import { ClientService } from '../../client/client.service';
import { Model } from '../../models/model';
import { Client } from '../../models/client';

@Component({
  selector: 'app-navigation',
  templateUrl: './navigation.component.html',
  styleUrls: ['./navigation.component.css']
})
export class NavigationComponent implements OnInit {
  isLoggedIn$: Observable<boolean> = this.authService.isLoggedIn;
  user: User | null = null;
  client: Client | null = null;
  profileImageUrl: string = '';
  profileGender: string = '';
  isProfileLoading: boolean = false;
  isUpgrading: boolean = false;
  hideNav: boolean = false;

  private hideOnRoutes = ['/login', '/register', '/verify-email'];

  constructor(
    private authService: AuthService,
    private userService: UserService,
    private router: Router,
    private modelService: ModelService,
    private clientService: ClientService,
    private dialog: MatDialog,
    private shareService: ShareService,
    private snackBar: MatSnackBar
  ) {
    this.router.events.pipe(filter(event => event instanceof NavigationEnd)).subscribe((event: any) => {
      this.hideNav = this.hideOnRoutes.some(route => this.router.url.startsWith(route));
    });
  }

  onShareReferral(): void {
    if (!this.user || !this.user.referral_code) { return; }
    const referralLink = this.shareService.getReferralLink(this.user.referral_code || '');
    this.dialog.open(ShareModelComponent, {
      data: { referralLink: referralLink },
      width: '350px',
      maxWidth: '95vw'
    });
  }

  ngOnInit() {
    // Always check and update user/profile image on every render
    this.authService.isLoggedIn.subscribe(() => {
      this.updateUserAndProfile();
    });
    // Also call once on init in case the observable doesn't emit
    this.updateUserAndProfile();
    // Set initial hideNav value
    this.hideNav = this.hideOnRoutes.some(route => this.router.url.startsWith(route));
  }

  private updateUserAndProfile() {
    const uid = this.authService.getCurrentUid();
    if (!uid) {
      this.user = null;
      this.profileImageUrl = '';
      this.profileGender = '';
      return;
    }

    this.isProfileLoading = true;

    this.userService.getUserById(uid).pipe(
      switchMap(user => {
        this.user = user;
        if (!user || !user.has_profile) {
          // User doc missing or no profile yet - use default placeholder
          this.applyDefaultProfile();
          return EMPTY;
        }
        // Fetch either model or client profile
        if (user.is_model) {
          return this.modelService.getModelById(uid).pipe(
            tap(model => this.applyModelProfile(model)),
            catchError(() => {
              this.applyDefaultProfile();
              return EMPTY;
            })
          );
        } else {
          return this.clientService.getClientByUserId(uid).pipe(
            tap(client => {
              if (!client) {
                this.applyDefaultProfile();
              } else {
                this.applyClientProfile(client);
              }
            }),
            catchError(() => {
              this.applyDefaultProfile();
              return EMPTY;
            })
          );
        }
      }),
      catchError(() => {
        this.applyDefaultProfile();
        return of(null);
      })
    ).subscribe({
      complete: () => { this.isProfileLoading = false; },
      error: () => { this.isProfileLoading = false; }
    });
  }

  private applyModelProfile(model: Model) {
    this.profileGender = model.gender;
    this.profileImageUrl = model.profile_image_url && model.profile_image_url.trim() !== ''
      ? model.profile_image_url
      : (model.gender === 'Male' ? 'assets/images/placeholder_male_profile_image.png' : 'assets/images/placeholder_profile_image.png');
  }

  private applyClientProfile(client: Client) {
    this.client = client;
    this.profileGender = client.gender;
    this.profileImageUrl = client.profile_image_url && client.profile_image_url.trim() !== ''
      ? client.profile_image_url
      : (client.gender === 'Male' ? 'assets/images/placeholder_male_profile_image.png' : 'assets/images/placeholder_profile_image.png');
  }

  private applyDefaultProfile() {
    this.client = null;
    this.profileGender = '';
    this.profileImageUrl = 'assets/images/placeholder_profile_image.png';
  }

  onLogout() {
    this.authService.logout();
  }

  onProfileClick() {
    if (!this.user) return;
    const uid = this.user.user_id;
    if (this.user.is_model) {
      this.router.navigate([`/models/${uid}/edit`]);
    } else {
      this.router.navigate([`/clients/${uid}/edit`]);
    }
  }

  onViewMyProfileClick() {
    if (!this.user || !this.user.is_model) return;
    this.router.navigate(['/models', this.user.user_id]);
  }

  onContentManagerClick() {
    if (!this.user || !this.user.is_model) return;
    const uid = this.user.user_id;
    this.router.navigate([`/content-manager`]);
  }

  /** Opens confirmation dialog and upgrades client → model on confirm */
  onBecomeModel(): void {
    if (!this.user || this.user.is_model || !this.client) return;

    const dialogRef = this.dialog.open(BecomeModelDialogComponent, {
      width: '420px',
      maxWidth: '95vw',
      disableClose: true
    });

    dialogRef.afterClosed().subscribe(confirmed => {
      if (!confirmed || !this.client) return;

      this.isUpgrading = true;
      this.userService.upgradeClientToModel(this.client).subscribe({
        next: () => {
          this.isUpgrading = false;
          this.snackBar.open('Welcome! Your model profile is ready.', 'Close', { duration: 7000 });
          // Refresh local user state and redirect to model edit page
          this.updateUserAndProfile();
          this.router.navigate([`/models/${this.user!.user_id}/edit`]);
        },
        error: (err) => {
          this.isUpgrading = false;
          this.snackBar.open('Upgrade failed. Please try again.', 'Close', { duration: 7000 });
          console.error('Client→Model upgrade error', err);
        }
      });
    });
  }
}
